import type { KeyboardEvent, ReactNode } from "react";
import { withTimeSystem } from "../../constants/orbitMetadata";
import { getElementDescendant } from "../../helpers/utils";
import { formatDate } from "../events/eventEvolutionUtils";
import { InfoField, type InfoFieldTone } from "../InfoField";
import { ConjunctionObjectTitle } from "./ConjunctionObjectTitle";
import {
  getConjunctionMetricTone,
  getDisplayValue,
  getFormattedDisplayValue,
  getNestedDisplayValue,
  getNestedValue,
  getProbabilityFamilyTone,
  getRiskLabel,
  getRiskTone,
  type ConjunctionRiskConfig,
  type RiskTone,
} from "./conjunctionUiUtils";

export interface ConjunctionSummaryCardField {
  label: string;
  value?: ReactNode;
  tone?: InfoFieldTone;
  strong?: boolean;
  tooltip?: string;
}

interface ConjunctionSummaryCardProps {
  accentClassName?: string;
  actions?: ReactNode;
  className?: string;
  conjunction?: unknown;
  formatters?: unknown;
  headerPill?: ReactNode;
  riskConfig?: ConjunctionRiskConfig;
  onClick?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  primaryObject?: ReactNode;
  secondaryObject?: ReactNode;
  summaryFields?: ConjunctionSummaryCardField[];
}

const riskPillClassName: Record<RiskTone, string> = {
  danger: "bg-red-100 text-red-700",
  muted: "bg-slate-100 text-slate-600",
  warning: "bg-amber-100 text-amber-700",
};

const riskCardClassName: Record<RiskTone, string> = {
  danger: "border-red-200 shadow-red-100/70",
  muted: "border-slate-200 shadow-slate-100/70",
  warning: "border-amber-200 shadow-amber-100/70",
};

const riskAccentClassName: Record<RiskTone, string> = {
  danger: "bg-red-500",
  muted: "bg-slate-300",
  warning: "bg-amber-400",
};

const getObjectValue = (
  conjunction: unknown,
  orbitElement: "target" | "chaser",
  path: string[],
) => getElementDescendant(conjunction, [orbitElement, ...path]);

const renderLegacyObjectCard = (
  conjunction: unknown,
  orbitElement: "target" | "chaser",
) => {
  const sourceValue = getDisplayValue(
    getObjectValue(conjunction, orbitElement, ["sourceOfInformation"]),
  );
  const updateTimeValue = getDisplayValue(
    getElementDescendant(conjunction, [`${orbitElement}OrbitUpdateTime`]),
  );

  return (
    <div className="min-w-0 max-w-[280px] rounded-lg border border-slate-200 bg-slate-50/80 px-2 py-1.5">
      <div className="mb-1 text-[11px] font-semibold uppercase text-slate-500">
        {orbitElement === "target" ? "primary" : "secondary"}
      </div>
      <ConjunctionObjectTitle
        objectId={getObjectValue(conjunction, orbitElement, [
          "spaceObject",
          "id",
        ])}
        cospar={getObjectValue(conjunction, orbitElement, [
          "spaceObject",
          "cosparId",
        ])}
        name={getObjectValue(conjunction, orbitElement, [
          "spaceObject",
          "name",
        ])}
        norad={getObjectValue(conjunction, orbitElement, [
          "spaceObject",
          "noradId",
        ])}
        sourceOfInformation={sourceValue}
        updateTime={updateTimeValue}
      />
    </div>
  );
};

export const ConjunctionSummaryCard = ({
  accentClassName,
  actions,
  className = "",
  conjunction,
  formatters,
  headerPill,
  riskConfig,
  onClick,
  onKeyDown,
  primaryObject,
  secondaryObject,
  summaryFields,
}: ConjunctionSummaryCardProps) => {
  const isInteractive = Boolean(onClick);
  const legacyRiskTone = conjunction
    ? getRiskTone(getNestedValue(conjunction, ["riskLevel"]))
    : "muted";
  const resolvedAccentClassName =
    accentClassName ??
    (conjunction ? riskAccentClassName[legacyRiskTone] : undefined);
  const resolvedClassName = `${
    conjunction ? riskCardClassName[legacyRiskTone] : ""
  } ${className}`.trim();
  const resolvedHeaderPill =
    headerPill ??
    (conjunction ? (
      <div className="flex items-center gap-2">
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase ${riskPillClassName[legacyRiskTone]}`}
        >
          {getRiskLabel(getNestedValue(conjunction, ["riskLevel"]))}
        </span>
        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
          {withTimeSystem("Execution Epoch")}:{" "}
          {formatDate(getNestedDisplayValue(conjunction, ["executionEpoch"]))}
        </span>
      </div>
    ) : null);
  const resolvedPrimaryObject =
    primaryObject ??
    (conjunction ? renderLegacyObjectCard(conjunction, "target") : null);
  const resolvedSecondaryObject =
    secondaryObject ??
    (conjunction ? renderLegacyObjectCard(conjunction, "chaser") : null);
  const resolvedSummaryFields =
    summaryFields ??
    (conjunction && formatters
      ? [
          {
            label: "MISS DIST.",
            value: getFormattedDisplayValue(
              conjunction,
              "missDistance.value",
              formatters,
            ),
            strong: true,
            tone: getConjunctionMetricTone(
              getDisplayValue(getNestedValue(conjunction, ["missDistance"])),
              riskConfig,
              "minDist",
            ),
          },
          {
            label: "RADIAL",
            value: getFormattedDisplayValue(
              conjunction,
              "radialDistance.value",
              formatters,
            ),
            strong: true,
            tone: getConjunctionMetricTone(
              getDisplayValue(getNestedValue(conjunction, ["radialDistance"])),
              riskConfig,
              "radDist",
            ),
          },
          {
            label: "ALONG",
            value: getFormattedDisplayValue(
              conjunction,
              "alongTrackDistance.value",
              formatters,
            ),
            strong: true,
            tone: getConjunctionMetricTone(
              getNestedValue(conjunction, ["alongTrackDistance", "value"]),
              riskConfig,
              "alongDist",
            ),
          },
          {
            label: "CROSS",
            value: getFormattedDisplayValue(
              conjunction,
              "crossTrackDistance.value",
              formatters,
            ),
            strong: true,
            tone: getConjunctionMetricTone(
              getNestedValue(conjunction, ["crossTrackDistance", "value"]),
              riskConfig,
              "crossDist",
            ),
          },
          {
            label: "POC",
            value: getFormattedDisplayValue(
              conjunction,
              "collisionProbability.value",
              formatters,
            ),
            strong: true,
            tone: getProbabilityFamilyTone(
              getDisplayValue(
                getNestedValue(conjunction, ["collisionProbability"]),
              ),
              riskConfig,
            ),
          },
          {
            label: "SPOC",
            value: getFormattedDisplayValue(
              conjunction,
              "scaledCollisionProbability.value",
              formatters,
            ),
            strong: true,
            tone: getProbabilityFamilyTone(
              getDisplayValue(
                getNestedValue(conjunction, ["collisionProbability"]),
              ),
              riskConfig,
            ),
          },
          {
            label: "MPOC",
            value: getFormattedDisplayValue(
              conjunction,
              "maximumCollisionProbability.value",
              formatters,
            ),
            strong: true,
            tone: getProbabilityFamilyTone(
              getDisplayValue(
                getNestedValue(conjunction, ["collisionProbability"]),
              ),
              riskConfig,
            ),
          },
        ]
      : []);

  const interactiveClassName = isInteractive
    ? "cursor-pointer transition duration-200 hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-lg hover:ring-1 hover:ring-sky-200/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2"
    : "transition duration-200";

  return (
    <article
      className={`relative flex w-full min-w-0 flex-col overflow-hidden rounded-lg border bg-white p-3 shadow-sm ${interactiveClassName} ${resolvedClassName}`}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={isInteractive ? "link" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
    >
      {resolvedAccentClassName ? (
        <span
          className={`absolute inset-y-0 left-0 w-1 ${resolvedAccentClassName}`}
        />
      ) : null}

      <header className="flex min-w-0 flex-wrap items-center justify-between gap-2 border-b border-slate-200/70 pb-2 pl-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {resolvedHeaderPill}
        </div>

        {actions ? (
          <div
            className="flex items-center gap-1"
            onClick={(event) => event.stopPropagation()}
          >
            {actions}
          </div>
        ) : null}
      </header>

      <div className="grid min-w-0 gap-3 pt-3 lg:grid-cols-[max-content_max-content_minmax(0,1fr)] lg:items-stretch">
        <div className="self-stretch justify-self-start">
          {resolvedPrimaryObject}
        </div>

        <div className="self-stretch justify-self-start">
          {resolvedSecondaryObject}
        </div>

        <div className="flex min-w-0 flex-nowrap items-stretch gap-2 overflow-x-auto pr-1">
          {resolvedSummaryFields.length > 0 ? (
            <div className="flex min-w-0 flex-1 flex-nowrap items-stretch gap-2">
              {resolvedSummaryFields.map((field) => (
                <InfoField
                  fillHeight
                  className="h-full min-w-0 !flex-1 basis-0 justify-center items-center"
                  key={field.label}
                  label={field.label}
                  strong={field.strong}
                  strongClassName="text-sm"
                  tone={field.tone}
                  tooltip={field.tooltip}
                  value={field.value}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
};
